import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { slugify } from "@/lib/slugify";

export interface EmployeeAllowanceTypeRecord {
  id: ID;
  code: string;
  label: string;
  sortOrder: number;
  createdAt: string;
}

const LS_KEY = "inventory.employeeAllowanceTypes.v1";
const BOOT_KEY = "inventory.employeeAllowanceTypes.seeded.v1";

type EmployeeAllowanceTypeApiRecord = {
  id?: string | number;
  code?: string | null;
  label?: string | null;
  sort_order?: number | null;
  sortOrder?: number | null;
  created_at?: string | null;
  createdAt?: string | null;
};

type ListApiResponse = {
  data?: EmployeeAllowanceTypeApiRecord[];
};

type UpsertApiResponse = {
  message?: string;
  data?: EmployeeAllowanceTypeApiRecord;
};

type DeleteApiResponse = { message?: string };

export type UpsertEmployeeAllowanceTypeResult = {
  record: EmployeeAllowanceTypeRecord;
  message: string;
};

export type DeleteEmployeeAllowanceTypeResult = {
  success: boolean;
  message: string;
};

function dedupeById(items: EmployeeAllowanceTypeRecord[]): EmployeeAllowanceTypeRecord[] {
  const byId = new Map<ID, EmployeeAllowanceTypeRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function mapApiRecord(record: EmployeeAllowanceTypeApiRecord): EmployeeAllowanceTypeRecord {
  const sort =
    Number(record.sort_order ?? record.sortOrder ?? 0) || 0;
  const created =
    String(record.created_at ?? record.createdAt ?? new Date().toISOString());
  return {
    id: String(record.id ?? ""),
    code: String(record.code ?? "").trim(),
    label: String(record.label ?? "").trim(),
    sortOrder: sort,
    createdAt: created,
  };
}

function load(): EmployeeAllowanceTypeRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as EmployeeAllowanceTypeRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: EmployeeAllowanceTypeRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

function extractRowsFromResponse(body: unknown): EmployeeAllowanceTypeApiRecord[] {
  if (Array.isArray(body)) return body as EmployeeAllowanceTypeApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;

  const fromBlock = (block: unknown): EmployeeAllowanceTypeApiRecord[] | null => {
    if (Array.isArray(block)) return block as EmployeeAllowanceTypeApiRecord[];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const inner = (block as Record<string, unknown>).data;
      if (Array.isArray(inner)) return inner as EmployeeAllowanceTypeApiRecord[];
    }
    return null;
  };

  for (const key of [
    "data",
    "items",
    "employee_allowance_types",
    "allowance_types",
    "results",
  ]) {
    const hit = fromBlock(root[key]);
    if (hit) return hit;
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function extractAllowanceTypeFromUpsertBody(
  body: unknown,
): EmployeeAllowanceTypeApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const block = data as Record<string, unknown>;
    if ("id" in block || typeof block.label === "string" || typeof block.code === "string") {
      return block as unknown as EmployeeAllowanceTypeApiRecord;
    }
    const nested = block.employee_allowance_type ?? block.allowance_type;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      return nested as unknown as EmployeeAllowanceTypeApiRecord;
    }
  }
  const top = root.employee_allowance_type ?? root.allowance_type;
  if (top && typeof top === "object" && !Array.isArray(top)) {
    return top as unknown as EmployeeAllowanceTypeApiRecord;
  }
  if ("id" in root && root.id != null) {
    return root as unknown as EmployeeAllowanceTypeApiRecord;
  }
  return null;
}

export function listEmployeeAllowanceTypeRecords(): EmployeeAllowanceTypeRecord[] {
  return load().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.label.localeCompare(b.label));
}

export function getEmployeeAllowanceTypeById(id: ID): EmployeeAllowanceTypeRecord | undefined {
  return load().find((t) => t.id === id);
}

/** Match API / legacy allowance name to a catalog row (case-insensitive label). */
export function findEmployeeAllowanceTypeIdByLabel(needle: string): string {
  const t = needle.trim().toLowerCase();
  if (!t) return "";
  const hit = load().find((x) => x.label.toLowerCase() === t);
  return hit?.id ?? "";
}

export function codeExists(code: string, exceptId?: ID): boolean {
  const c = slugify(code);
  return load().some((t) => slugify(t.code) === c && t.id !== exceptId);
}

const EMPLOYEE_ALLOWANCE_TYPES_PER_PAGE = 20;

export async function syncEmployeeAllowanceTypesFromApi(): Promise<EmployeeAllowanceTypeRecord[]> {
  try {
    const collected: EmployeeAllowanceTypeApiRecord[] = [];
    let page = 1;
    let lastPage = 1;
    do {
      const response = await api.get<ListApiResponse>("/api/v1/employee-allowance-types", {
        params: { page, per_page: EMPLOYEE_ALLOWANCE_TYPES_PER_PAGE },
      });
      const body = response.data;
      collected.push(...extractRowsFromResponse(body));
      lastPage = readLastPageFromListBody(body) ?? 1;
      page += 1;
    } while (page <= lastPage && page <= 50);

    const records = dedupeById(
      collected.map(mapApiRecord).filter((r) => Boolean(r.id)),
    );
    save(records);
  } catch (error) {
    console.error("[EmployeeAllowanceTypes] sync failed", error);
    throw error;
  }
  return listEmployeeAllowanceTypeRecords();
}

export async function createEmployeeAllowanceType(data: {
  code: string;
  label: string;
  sortOrder?: number;
}): Promise<UpsertEmployeeAllowanceTypeResult> {
  const sortOrder = data.sortOrder ?? 0;
  const payload = {
    code: slugify(data.code),
    label: data.label.trim(),
    sort_order: sortOrder,
  };
  const response = await api.post<UpsertApiResponse>("/api/v1/employee-allowance-types", payload);
  const created = extractAllowanceTypeFromUpsertBody(response.data);
  if (!created || created.id == null) {
    throw new Error("Invalid create allowance type response.");
  }
  const record = mapApiRecord(created);
  const items = load();
  const idx = items.findIndex((t) => t.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.push(record);
  save(items);
  return {
    record,
    message: response.data?.message || "Allowance type created.",
  };
}

export async function updateEmployeeAllowanceType(
  id: ID,
  data: { code: string; label: string; sortOrder?: number },
): Promise<UpsertEmployeeAllowanceTypeResult | null> {
  const items = load();
  const i = items.findIndex((t) => t.id === id);
  if (i === -1) return null;
  const payload = {
    code: slugify(data.code) || items[i].code,
    label: data.label.trim(),
    sort_order: data.sortOrder ?? items[i].sortOrder,
  };
  const response = await api.put<UpsertApiResponse>(`/api/v1/employee-allowance-types/${id}`, payload);
  let updated = extractAllowanceTypeFromUpsertBody(response.data);
  if (!updated) throw new Error("Invalid update allowance type response.");
  if (updated.id == null || String(updated.id) === "") {
    updated = { ...updated, id };
  }
  items[i] = mapApiRecord(updated);
  save(items);
  return {
    record: items[i],
    message: response.data?.message || "Allowance type updated.",
  };
}

export async function deleteEmployeeAllowanceType(id: ID): Promise<DeleteEmployeeAllowanceTypeResult> {
  const response = await api.delete<DeleteApiResponse>(`/api/v1/employee-allowance-types/${id}`);
  const items = load();
  const next = items.filter((t) => t.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return {
    success: removed,
    message:
      response.data?.message ||
      (removed ? "Allowance type removed." : "Allowance type was not found."),
  };
}

/** Seed default allowance names when storage is empty (same role as purchase cost types for products). */
export function seedEmployeeAllowanceTypesIfEmpty() {
  if (typeof localStorage === "undefined") return;
  if (localStorage.getItem(BOOT_KEY)) return;
  if (load().length > 0) {
    localStorage.setItem(BOOT_KEY, "1");
    return;
  }
  const now = new Date().toISOString();
  const seed: EmployeeAllowanceTypeRecord[] = [
    { id: "eat_medical", code: "medical", label: "Medical", sortOrder: 1, createdAt: now },
    { id: "eat_conveyance", code: "conveyance", label: "Conveyance", sortOrder: 2, createdAt: now },
    { id: "eat_house_rent", code: "house_rent", label: "House rent", sortOrder: 3, createdAt: now },
    { id: "eat_utilities", code: "utilities", label: "Utilities", sortOrder: 4, createdAt: now },
  ];
  save(seed);
  localStorage.setItem(BOOT_KEY, "1");
}
